import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assests/AM_Global_HD_Logo.png";
import { FiAlignRight, FiX } from "react-icons/fi";
import { goToCareersSite } from "../utils/careersSiteUrl";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSwitch from "./LanguageSwitch";

const SECTION_ORDER = ["our__services", "industries", "about__Us"];
const DEFAULT_SECTION_OFFSET = 90;

const navButtonReset = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
};

function scrollOffsetForSection(sectionId) {
  if (sectionId === "home") return 0;
  if (typeof window === "undefined") return 90;
  return window.innerWidth < 769 ? 70 : 90;
}

export default function Navbar() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolling, setIsScrolling] = useState(false);
  const [isActive, setIsActive] = useState("");
  const [toggleMenu, setToggleMenu] = useState(false);

  const scrollSpyPausedRef = useRef(false);
  const scrollSpyResumeTimerRef = useRef(null);

  const clearResumeTimer = () => {
    if (scrollSpyResumeTimerRef.current != null) {
      window.clearTimeout(scrollSpyResumeTimerRef.current);
      scrollSpyResumeTimerRef.current = null;
    }
  };

  const resumeScrollSpy = useCallback(() => {
    scrollSpyPausedRef.current = false;
    clearResumeTimer();
  }, []);

  const scheduleResumeScrollSpy = useCallback(() => {
    clearResumeTimer();
    const onScrollEnd = () => resumeScrollSpy();
    if (typeof window !== "undefined" && "onscrollend" in window) {
      window.addEventListener("scrollend", onScrollEnd, { once: true });
    }
    scrollSpyResumeTimerRef.current = window.setTimeout(onScrollEnd, 750);
  }, [resumeScrollSpy]);

  const scrollToSection = useCallback(
    (sectionId, offsetOverride) => {
      const offset =
        offsetOverride !== undefined ? offsetOverride : scrollOffsetForSection(sectionId);

      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: sectionId, scrollOffset: offset } });
        setToggleMenu(false);
        return;
      }

      const el = document.getElementById(sectionId);
      if (!el) return;

      scrollSpyPausedRef.current = true;
      setIsActive(sectionId === "home" ? "" : sectionId);
      setToggleMenu(false);

      window.scrollTo({
        top: Math.max(0, el.offsetTop - offset),
        behavior: "smooth",
      });
      scheduleResumeScrollSpy();
    },
    [location.pathname, navigate, scheduleResumeScrollSpy],
  );

  const computeActiveSection = useCallback(() => {
    const scrollPosition = window.scrollY;
    const line = scrollPosition + 96;
    let current = "";
    for (const sectionId of SECTION_ORDER) {
      const el = document.getElementById(sectionId);
      if (!el) continue;
      if (el.offsetTop <= line) current = sectionId;
    }
    return current;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolling(scrollPosition > 0);
      setToggleMenu(false);

      if (scrollSpyPausedRef.current || location.pathname !== "/") return;
      setIsActive(computeActiveSection());
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, computeActiveSection]);

  useEffect(() => {
    if (location.pathname !== "/" || scrollSpyPausedRef.current) return;
    const id = window.requestAnimationFrame(() => {
      setIsActive(computeActiveSection());
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, location.key, computeActiveSection]);

  const handleEnquire = () => {
    const el = document.getElementById("contact_us");
    if (!el) return;
    if (location.pathname !== "/") {
      navigate("/", {
        state: { scrollTo: "contact_us", scrollOffset: DEFAULT_SECTION_OFFSET },
      });
      setToggleMenu(false);
      return;
    }
    scrollSpyPausedRef.current = true;
    window.scrollTo({
      top: Math.max(0, el.offsetTop - DEFAULT_SECTION_OFFSET),
      behavior: "smooth",
    });
    scheduleResumeScrollSpy();
    setToggleMenu(false);
  };

  const handleCareers = () => {
    setToggleMenu(false);
    goToCareersSite();
  };

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setToggleMenu(false);
      return;
    }
    scrollToSection("home", 0);
  };

  useEffect(() => () => clearResumeTimer(), []);

  const SectionBtn = ({ sectionId, children, offsetOverride }) => (
    <button
      type="button"
      className={isActive === sectionId ? "Link_Style_Scroll" : "Link_Style"}
      style={navButtonReset}
      aria-current={isActive === sectionId ? "location" : undefined}
      onClick={() => scrollToSection(sectionId, offsetOverride)}
    >
      {children}
    </button>
  );

  const navLinks = (
    <>
      <SectionBtn sectionId="our__services">{t("nav.services")}</SectionBtn>
      <SectionBtn sectionId="industries">{t("nav.industries")}</SectionBtn>
      <SectionBtn sectionId="about__Us">{t("nav.about")}</SectionBtn>
      <button
        type="button"
        className="Link_Style"
        style={navButtonReset}
        onClick={handleCareers}
      >
        {t("nav.careers")}
      </button>
    </>
  );

  return (
    <nav
      className={
        isScrolling
          ? "NavBar_Main_Container Active_Header"
          : "NavBar_Main_Container"
      }
      style={{ backgroundColor: "var(--amg-surface)" }}
    >
          <div className="navbarInnerContainer">
            <div className="navbarLogoContainer">
              <button
                type="button"
                className="navbarLogoButton"
                onClick={goHome}
                aria-label={t("nav.homeAria")}
              >
                <img src={logo} alt="AM Global" />
              </button>
            </div>

            <div className="Navlink_Wrapper">{navLinks}</div>

            <div className="amgNavActions">
              <LanguageSwitch />
              <div onClick={handleEnquire} className="NavbarButtonContainer">
                {t("nav.enquire")}
              </div>
            </div>

            <div className="collapse_button_number">
              <div className={"NavLink_App_Smaller_Devices"}>
                {toggleMenu ? (
                  <div className="Toggle_Btn_CLose">
                    <FiX
                      color="white"
                      size={30}
                      onClick={() => setToggleMenu(false)}
                    />
                  </div>
                ) : (
                  <div className="Toggle_Btn_Open">
                    <FiAlignRight
                      color="white"
                      size={30}
                      onClick={() => setToggleMenu(true)}
                    />
                  </div>
                )}

                {toggleMenu ? (
                  <div className={"NavLink_App_Smaller_Overlay"}>
                    <div
                      className={"Navlink_App_Links_Wrapper_SmallScreen_links"}
                    >
                      {navLinks}
                      <LanguageSwitch />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
    </nav>
  );
}
